import { createEditor, Editor as SlateEditor, Node } from 'slate';
import { Slate, withReact } from 'slate-react';
import Editor from '../item-content/item-content-editor';
import forItemContent from './item-content-plugin';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { withHistory } from 'slate-history';
import {
  useCurrentItem,
  useData,
  useDataActions,
  useTimestamps
} from '../item-content/polling-context';
import migrateSlateValue from './migrate-slate-value';

/*
    A wrapper that provides slate context
*/
const ItemContentContext = (
  {
    children,
    examinee = false,
    examiner = false,
    interactionEnabled = true,
    placeholder = undefined, // optional. displays when no content
    readOnly = true,
    value: propsValue = undefined // optional. overrides examinee/examiner flags
  },
  ref
) => {
  const currentItem = useCurrentItem();
  const data = useData();
  const { updateData } = useDataActions();
  const { currentItemTimestamp } = useTimestamps();
  // The data hooks reach function elements before value updates via slate.
  // The timestamp copy gets uppdated at the same time as value, so it can be compared with the hook's timestamp value to check if slate is using the most recent content.
  const [currentItemTimestamp_copy, setCurrentItemTimestamp_copy] = useState(null);
  const [value, setValue] = useState(null); // local working copy of current item content document

  const editor = useMemo(() => {
    // create editor with plugins
    const editor = forItemContent(withHistory(withReact(createEditor())));

    // init custom properties
    editor.data = {};

    return editor;
  }, []);

  const defaultValue = [
    {
      type: 'paragraph',
      children: [
        {
          text: '',
          fontFamily: 'Roboto',
          fontSize: 20
        }
      ]
    }
  ];

  useImperativeHandle(ref, () => {
    return { getFileNames, getValue };
  });

  const getFileNames = () => {
    const fileNames = [];
    for (const [node] of Node.nodes(editor)) {
      switch (node.type) {
        default:
          break;
        case 'audio':
        case 'image':
        case 'video':
          fileNames.push(node.url.split('/').pop());
      }
    }
    return fileNames;
  };

  const getValue = () => {
    return value;
  };

  useLayoutEffect(() => {
    editor.interactionEnabled = interactionEnabled;
  }, [interactionEnabled, editor, editor.interactionEnabled]);
  useLayoutEffect(() => {
    editor.readOnly = readOnly;
  }, [readOnly, editor, editor.readOnly]);
  useLayoutEffect(() => {
    editor.sendData = updateData;
  }, [updateData, editor, editor.sendData]);
  useLayoutEffect(() => {
    // make a working copy of the current document
    let rawValue;
    if (propsValue) rawValue = propsValue;
    else if (examinee && currentItem) rawValue = currentItem.content.examinee.document;
    else if (examiner && currentItem) rawValue = currentItem.content.examiner.document;
    else rawValue = null;

    const newValue = migrateSlateValue(rawValue);
    setValue(newValue);

    // sync editor.children so Slate re-renders with the new content
    // (initialValue is only used on mount in slate-react 0.99+).
    // When the new item has null/empty content, reset to defaultValue so
    // the prior item's tree doesn't linger and render blank until Next.
    const nextChildren = Array.isArray(newValue) && newValue.length > 0 ? newValue : defaultValue;
    editor.children = nextChildren;
    // Force Slate to run its schema/normalization pass on the freshly-assigned
    // tree — direct mutation bypasses the validation that would otherwise
    // catch malformed migrated nodes (e.g. stray top-level text).
    SlateEditor.normalize(editor, { force: true });
    editor.onChange();

    // copy the timestamp
    setCurrentItemTimestamp_copy(currentItemTimestamp);
    // defaultValue is intentionally a fresh array literal each render and is
    // deliberately excluded from the deps. It's assigned to editor.children
    // above, and the following normalize()/onChange() mutate editor.children
    // in place — so a shared/stable default (module const or useMemo) could be
    // polluted by Slate and corrupt later resets and the slateValue fallback.
    // A pristine literal per render avoids that; adding it to deps would also
    // re-run this sync on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, currentItemTimestamp, examinee, examiner, propsValue, editor]);
  useLayoutEffect(() => {
    if (!editor.readOnly) return;

    // update editor's copy of data
    editor.data = data;

    // normalize all funcs
    Editor.normalizeAll(editor, 'function');
  }, [data, editor]);

  const slateValue = value || placeholder || defaultValue;

  if (!Array.isArray(slateValue)) {
    const source = propsValue
      ? 'propsValue'
      : examinee
        ? 'currentItem.content.examinee.document'
        : examiner
          ? 'currentItem.content.examiner.document'
          : 'placeholder/default';
    console.error(
      '[ItemContentContext] Invalid Slate value — expected an array of elements.',
      '\n  Source:',
      source,
      '\n  Item ID:',
      currentItem?.itemID ?? currentItem?._id ?? 'N/A',
      '\n  Item label:',
      currentItem?.label ?? 'N/A',
      '\n  Value:',
      JSON.stringify(slateValue).slice(0, 500)
    );
  }

  return (
    <Slate
      children={children}
      editor={editor}
      onChange={useCallback((value) => {
        setValue(value);
      }, [])}
      initialValue={slateValue}
      currentItemTimestamp={currentItemTimestamp_copy} // gets assigned to editor
    />
  );
};

export default forwardRef(ItemContentContext);
